import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DataGlance.scss';
import './PopulationToggle.scss';
import { API_BASE } from '../../config/environments/api';

// Hook to animate number increase
const useAnimatedNumber = (formattedValue, shouldAnimate = true, animationSpeed = 15) => {
  const [displayValue, setDisplayValue] = useState(formattedValue);
  const previousValueRef = useRef(formattedValue);
  const animationRef = useRef(null);

  useEffect(() => {
    // Store the original formatted value to use as template
    const originalValue = formattedValue;
    
    // Parse numeric value from formatted string
    const parseNumericValue = (val) => {
      if (val === null || val === undefined || val === 'N/A' || val === '' || val === '0') return 0;
      
      // Remove commas, spaces, and extract number part
      const cleaned = String(val).replace(/[,\s]/g, '');
      
      // Handle percentage, million (M), billion (B), etc.
      let multiplier = 1;
      let numStr = cleaned;
      
      if (cleaned.includes('M') && !cleaned.includes('(M)')) {
        multiplier = 1000000;
        numStr = cleaned.replace('M', '');
      } else if (cleaned.includes('B')) {
        multiplier = 1000000000;
        numStr = cleaned.replace('B', '');
      } else if (cleaned.includes('K')) {
        multiplier = 1000;
        numStr = cleaned.replace('K', '');
      } else if (cleaned.includes('%')) {
        numStr = cleaned.replace('%', '');
      }
      
      const num = parseFloat(numStr);
      return isNaN(num) ? 0 : num * multiplier;
    };

    const startValue = parseNumericValue(previousValueRef.current);
    const endValue = parseNumericValue(formattedValue);
    
    // If new value is empty/null/undefined, keep previous value (don't update)
    if (formattedValue === '' || formattedValue === null || formattedValue === undefined) {
      // Keep previous value - don't update displayValue
      return;
    }
    
    // If values are the same or animation disabled, update immediately
    // Also immediately update if transitioning to/from empty string (no animation for empty values)
    if (!shouldAnimate || startValue === endValue || formattedValue === 'N/A' || previousValueRef.current === 'N/A' ||
        (formattedValue === '' && previousValueRef.current !== '')) {
      setDisplayValue(formattedValue || '');
      previousValueRef.current = formattedValue || '';
      return;
    }

    // Clear any existing animation
    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // Calculate steps needed based on difference
    const difference = endValue - startValue;
    const absDifference = Math.abs(difference);
    
    // Calculate steps: more steps for larger differences, but cap at reasonable number
    let steps;
    if (absDifference < 100) {
      steps = Math.max(10, Math.floor(absDifference / 5));
    } else if (absDifference < 1000) {
      steps = Math.max(20, Math.floor(absDifference / 20));
    } else if (absDifference < 10000) {
      steps = Math.max(30, Math.floor(absDifference / 50));
    } else {
      steps = Math.max(40, Math.min(80, Math.floor(absDifference / 200)));
    }
    
    const stepSize = difference / steps;
    let currentStep = 0;

    // Format value during animation - preserve original format
    const formatAnimatedValue = (num) => {
      const originalStr = String(originalValue);
      
      // If original has 'M' (not in parentheses), format as million
      if (originalStr.includes('M') && !originalStr.includes('(M)')) {
        const millions = num / 1000000;
        return millions.toFixed(2) + 'M';
      }
      // If original has 'B', format as billion
      if (originalStr.includes('B')) {
        const billions = num / 1000000000;
        return billions.toFixed(2) + 'B';
      }
      // If original has 'K', format as thousand
      if (originalStr.includes('K')) {
        const thousands = num / 1000;
        return thousands.toFixed(2) + 'K';
      }
      // If original has '%', format as percentage
      if (originalStr.includes('%')) {
        return num.toFixed(2) + '%';
      }
      
      // Default: format with commas (Indian numbering system style)
      // Try to match the format from formatIndianNumber
      const numStr = num.toFixed(2);
      const parts = numStr.split('.');
      let integerPart = parts[0];
      const decimalPart = parts[1] || '';
      
      // Add commas in Indian format: first comma after 3 digits, then every 2 digits
      let formatted = '';
      const len = integerPart.length;
      
      if (len <= 3) {
        formatted = integerPart;
      } else {
        // First 3 digits from right
        formatted = integerPart.slice(-3);
        integerPart = integerPart.slice(0, -3);
        
        // Then add commas every 2 digits
        while (integerPart.length > 0) {
          if (integerPart.length >= 2) {
            formatted = integerPart.slice(-2) + ',' + formatted;
            integerPart = integerPart.slice(0, -2);
          } else {
            formatted = integerPart + ',' + formatted;
            integerPart = '';
          }
        }
      }
      
      // Add decimal part if exists (only if number has decimals)
      return decimalPart ? formatted + '.' + decimalPart : formatted;
    };

    // Animate the number
    animationRef.current = setInterval(() => {
      currentStep++;
      const currentValue = startValue + (stepSize * currentStep);
      
      if (currentStep >= steps) {
        setDisplayValue(formattedValue);
        previousValueRef.current = formattedValue;
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
      } else {
        setDisplayValue(formatAnimatedValue(currentValue));
      }
    }, animationSpeed);

    // Cleanup
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [formattedValue, shouldAnimate, animationSpeed]);

  return displayValue;
};

const DataGlance = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('region'); // 'region' or 'country'
  const [hoveredArea, setHoveredArea] = useState(null);
  const [africaData, setAfricaData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [populationToggle, setPopulationToggle] = useState('access'); // 'access' or 'without'
  const mapRef = useRef(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const previousYearRef = useRef(null);
  const previousRegionRef = useRef(null);
  const previousCountryRef = useRef(null);

  // State for available filters
  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);

  const isMobile = window.innerWidth <= 480;

  // Initialize with empty data - will be populated from API only
  const [currentData, setCurrentData] = useState({
    countries: 0,
    gdp: '0',
    electricityAccess: '0',
    population: '0',
    perCapitaElectricity: 'N/A',
    cleanCooking: '0',
    electricityImport: 'N/A',
    electricityGeneration: 'N/A',
    electricityFossilFuels: 'N/A',
    renewableSources: 'N/A',
    solarWindTideWaveOtherSources: 'N/A',
    populationWithAccessUrban: 'N/A',
    populationWithAccessRural: 'N/A',
    populationWithoutAccessUrban: 'N/A',
    populationWithoutAccessRural: 'N/A'
  });

  // Helper function to format numbers in Indian numbering system (lakhs/crores)
  const formatIndianNumber = (num, showDecimals = true) => {
    // Check if number is a whole number (no decimal part)
    const isWholeNumber = num % 1 === 0;
    
    // Format to 2 decimal places only if showDecimals is true AND number has decimals
    const numStr = (showDecimals && !isWholeNumber) ? num.toFixed(2) : num.toString();
    
    // Split into integer and decimal parts
    const parts = numStr.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1] || '';
    
    // Add commas in Indian format: first comma after 3 digits, then every 2 digits
    let formatted = '';
    const len = integerPart.length;
    
    if (len <= 3) {
      formatted = integerPart;
    } else {
      // First 3 digits from right
      formatted = integerPart.slice(-3);
      integerPart = integerPart.slice(0, -3);
      
      // Then add commas every 2 digits
      while (integerPart.length > 0) {
        if (integerPart.length >= 2) {
          formatted = integerPart.slice(-2) + ',' + formatted;
          integerPart = integerPart.slice(0, -2);
        } else {
          formatted = integerPart + ',' + formatted;
          integerPart = '';
        }
      }
    }
    
    // Add decimal part if exists (only if number has decimals)
    return decimalPart ? formatted + '.' + decimalPart : formatted;
  };

  // Helper function to format numbers with commas for non-percentage values
  const formatNumber = (value, isPercentage = false) => {
    if (value === 'N/A' || value === null || value === undefined) return 'N/A';
    if (isPercentage) return value; // Don't format percentages (clean cooking)
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    // For zero, return '0' without decimals
    if (numValue === 0) {
      return '0';
    }
    
    // Use Indian numbering format with 2 decimal places
    return formatIndianNumber(numValue, true);
  };

  // Helper function to format GDP values in millions
  const formatGDP = (value) => {
    if (value === 'N/A' || value === null || value === undefined) return 'N/A';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    // Convert to millions (divide by 1,000,000)
    const millionValue = numValue / 1000000;
    
    // For zero, return '0' without decimals
    if (millionValue === 0) {
      return '0';
    }
    
    // Use Indian numbering format with 2 decimal places
    return formatIndianNumber(millionValue, true);
  };

  // Helper function to convert country name to URL format
  const countryToUrl = (countryName) => {
    if (!countryName) return '';
    return countryName.toLowerCase().replace(/\s+/g, '-');
  };

  // Helper function to calculate total population from four indicators
  const calculateTotalPopulation = (withAccessUrban, withAccessRural, withoutAccessUrban, withoutAccessRural) => {
    // Helper to parse a value (handles 'N/A', null, undefined, strings, numbers)
    const parseValue = (val) => {
      if (val === null || val === undefined || val === '' || val === 'N/A') {
        return 0;
      }
      const numValue = typeof val === 'string' ? parseFloat(val) : Number(val);
      return isNaN(numValue) ? 0 : numValue;
    };

    // Sum all four components
    const total = parseValue(withAccessUrban) + 
                  parseValue(withAccessRural) + 
                  parseValue(withoutAccessUrban) + 
                  parseValue(withoutAccessRural);
    
    return total;
  };

  const convertValue = (value) => {
    // Check for null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    // Convert to number for comparison
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    // Check if value is 0 (including 0.0, '0', etc.)
    if (numValue === 0 && !isNaN(numValue)) {
      return '0';
    }
    // For any other valid number, format with up to 2 decimal places
    if (typeof numValue === 'number' && !isNaN(numValue)) {
      // Format to 2 decimal places and remove trailing zeros
      const formatted = numValue.toFixed(2);
      // Remove trailing zeros and decimal point if not needed
      return formatted.replace(/\.?0+$/, '');
    }
    // For strings that are not numeric, return as is (fallback)
    return value.toString();
  };

  // Fetch available years on component mount
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch(`${API_BASE}/years`);
        
        if (response.ok) {
          const data = await response.json();
          
          // Ensure unique years and convert to numbers
          const uniqueYears = [...new Set(data.years.map(year => Number(year)))].sort((a, b) => b - a);
          
          setAvailableYears(uniqueYears);
          if (uniqueYears.length > 0) {
            // Set 2021 as default year, fallback to most recent if 2021 not available
            const defaultYear = uniqueYears.includes(2025) ? 2025 : uniqueYears[0];
            setSelectedYear(defaultYear);
          }
        }
      } catch (error) {
      }
    };

    fetchYears();
  }, []);

    // Load Africa GeoJSON
    useEffect(() => {
      fetch('/map/africa.geo.json')
        .then(response => response.json())
        .then(data => {
          setAfricaData(data);
        })
        .catch(error => {
        });
    }, []);

  // Fetch available regions and countries on year change
  useEffect(() => {
    if (!selectedYear) return;
    async function fetchFilters() {
      try {
        // Regions
        const regionResp = await fetch(`${API_BASE}/data-glance/regions?year=${selectedYear}`);
        let regions = [];
        if (regionResp.ok) {
          const { regions: regionList } = await regionResp.json();
          const dbToFrontendRegion = {
            'Eastern Africa': 'East Africa',
            'Northern Africa': 'North Africa',
            'Western Africa': 'West Africa',
            'Southern Africa': 'Southern Africa',
            'Central Africa': 'Central Africa'
          };
          regions = Array.from(new Set((regionList || []).map(r => dbToFrontendRegion[r] || r))).sort();
        }
        setAvailableRegions(regions);
        // Retain region selection if still valid; otherwise clear
        setSelectedRegion(prev => (prev && regions.includes(prev) ? prev : (prev ? null : prev)));

        // Countries
        const countryResp = await fetch(`${API_BASE}/data-glance/countries?year=${selectedYear}`);
        let countries = [];
        if (countryResp.ok) {
          const { countries: countryList } = await countryResp.json();
          countries = (countryList || []).sort();
        }
        setAvailableCountries(countries);
        // Retain country selection if still valid; otherwise clear
        setSelectedCountry(prev => (prev && countries.includes(prev) ? prev : (prev ? null : prev)));
      } catch (e) {}
    }
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const handleRegionChange = e => {
    const region = e.target.value;
    setSelectedRegion(region);
    setSelectedCountry(null);
    setViewMode('region');
    if (region) fetchDataFromAPI(region, null);
  };
  const handleCountryChange = e => {
    const country = e.target.value;
    setSelectedCountry(country);
    setSelectedRegion(null);
    setViewMode('country');
    if (country) fetchDataFromAPI(null, country);
  };

  const getRegionStyle = (feature) => {
    const subregion = feature.properties.subregion;
    
    // Map GeoJSON subregion names to API region names (same as backend)
    const subregionMapping = {
      'Middle Africa': 'Central Africa',
      'Eastern Africa': 'East Africa',
      'Northern Africa': 'North Africa',
      'Southern Africa': 'Southern Africa',
      'Western Africa': 'West Africa'
    };
    
    const apiRegion = subregionMapping[subregion] || subregion;
    // const isAvailable = availableRegions.includes(apiRegion);
    // if (!isAvailable) {
    //   return {
    //     fillColor: '#fff',
    //     weight: 1,
    //     opacity: 0.2,
    //     color: '#CCCCCC',
    //     fillOpacity: 1,
    //     dashArray: '3,3'
    //   };
    // }
    
    // Light versions of region colors for unselected state
    const lightRegionColors = {
      'North Africa': '#FFEB99',   // Brighter soft gold
      'West Africa': '#FFC2B3',    // Brighter warm coral/salmon
      'Central Africa': '#D9B3FF', // Brighter lavender
      'East Africa': '#B3E6B3',    // Brighter soft green
      'Southern Africa': '#B3D9FF' // Brighter sky blue
    };

    // Check if this region is selected
    const isSelected = selectedRegion === apiRegion;

    // Darker versions of region colors for selected state
    const darkerRegionColors = {
      'North Africa': '#FFD700',  // Dark Gold
      'West Africa': '#FF4B4B',   // Dark Salmon/Red
      'Central Africa': '#8C4BB0', // Dark Purple
      'East Africa': '#3CBF4B',    // Dark Green
      'Southern Africa': '#2980B9' // Dark Blue
    };

    return {
      fillColor: isSelected ? (darkerRegionColors[apiRegion] || '#9A9A9A') : (lightRegionColors[apiRegion] || '#F8F8F8'),
      weight: isSelected ? 1.2 : 1, // Thicker border for selected region
      opacity: 1,
      color: isSelected ? '#4c4949' : '#CCCCCC', // Darker border for selected, light border for unselected
      fillOpacity: isSelected ? 0.9 : 0.8,
      dashArray: isSelected ? null : '3,3' // Dashed border for unselected, solid for selected
    };
  };

  const getCountryStyle = (feature) => {
    const countryName = feature.properties.name;
    // const isAvailable = availableCountries.includes(countryName);
    // if (!isAvailable) {
    //   // No fill, but strong orange border (dashed)
    //   return {
    //     fillColor: '#fff',
    //     weight: 1.6,
    //     opacity: 1,
    //     color: '#D48019',
    //     fillOpacity: 1,
    //     dashArray: '3,3'
    //   };
    // }
    // Reverted: use prior color scheme for country polygons
    return {
      fillColor: selectedCountry === countryName ? '#F39C12' : '#F39C12',
      weight: selectedCountry === countryName ? 1.8 : 1.2,
      fillOpacity: selectedCountry === countryName ? 1 : 0.25,
      color: '#D48019',
      opacity: 1,
      dashArray: selectedCountry === countryName ? null : '3,3',
    };
  };

  // Fetch data from API
  const fetchDataFromAPI = useCallback(async (regionName = null, countryName = null) => {
    if (!selectedYear) {
      return;
    }

    try {
      setLoading(true);
      
      let apiUrl;
      let response;
      
      // Use unified API for both region and country data
      if (viewMode === 'country' && countryName) {
        apiUrl = `${API_BASE}/data-glance-region-and-country-wise?year=${selectedYear}&country=${encodeURIComponent(countryName)}`;
      } else if (viewMode === 'region' && regionName) {
        apiUrl = `${API_BASE}/data-glance-region-and-country-wise?year=${selectedYear}&region=${encodeURIComponent(regionName)}`;
      } else {
        // If no region/country provided, we can't fetch data
          setCurrentData({
            countries: 0,
            gdp: '0',
            electricityAccess: '0',
            population: '0',
            perCapitaElectricity: 'N/A',
            cleanCooking: '0',
            electricityImport: 'N/A',
          electricityGeneration: 'N/A',
          electricityFossilFuels: 'N/A',
          renewableSources: 'N/A',
          solarWindTideWaveOtherSources: 'N/A',
          populationWithAccessUrban: 'N/A',
          populationWithAccessRural: 'N/A',
          populationWithoutAccessUrban: 'N/A',
          populationWithoutAccessRural: 'N/A'
          });
        setLoading(false);
        return;
      }
      
      response = await fetch(apiUrl);

      if (response.ok) {
        const responseData = await response.json();
        // Handle the data-glance-region-and-country-wise API response format
        if (Array.isArray(responseData) && responseData.length > 0) {
          const data = responseData[0]; // Get the first (and only) result
          
          // Calculate population components first
          const withAccessUrban = convertValue(data.population_with_access_urban);
          const withAccessRural = convertValue(data.population_with_access_rural);
          const withoutAccessUrban = convertValue(data.population_without_access_urban);
          const withoutAccessRural = convertValue(data.population_without_access_rural);
          
          // Calculate total population by summing all four components (frontend calculation for accuracy)
          const totalPopulation = calculateTotalPopulation(
            data.population_with_access_urban,
            data.population_with_access_rural,
            data.population_without_access_urban,
            data.population_without_access_rural
          );
          
          setCurrentData({
            countries: data.no_of_countries !== null && data.no_of_countries !== undefined ? data.no_of_countries : (viewMode === 'country' ? 1 : 0),
            gdp: convertValue(data.gdp_per_capita),
            electricityAccess: convertValue(data.electricity_access),
            population: totalPopulation > 0 ? convertValue(totalPopulation) + 'M' : '0M',
            perCapitaElectricity: convertValue(data.electricity_final_consumption_per_capita),
            cleanCooking: convertValue(data.clean_cooking_access),
            electricityImport: convertValue(data.electricity_import),
            electricityGeneration: convertValue(data.electricity_generation),
            electricityFossilFuels: convertValue(data.electricity_fossil_fuels),
            renewableSources: convertValue(data.renewable_sources),
            solarWindTideWaveOtherSources: convertValue(data.solar_wind_tide_wave_other_sources),
            populationWithAccessUrban: withAccessUrban,
            populationWithAccessRural: withAccessRural,
            populationWithoutAccessUrban: withoutAccessUrban,
            populationWithoutAccessRural: withoutAccessRural
          });
        } else {
          throw new Error('No data found for the selected criteria');
        }
      } else {
        setCurrentData({
          countries: 0,
          gdp: '0',
          electricityAccess: '0',
          population: '0',
          perCapitaElectricity: 'N/A',
          cleanCooking: '0',
          electricityImport: 'N/A',
          electricityGeneration: 'N/A',
          electricityFossilFuels: 'N/A',
          renewableSources: 'N/A',
          solarWindTideWaveOtherSources: 'N/A',
          populationWithAccessUrban: 'N/A',
          populationWithAccessRural: 'N/A',
          populationWithoutAccessUrban: 'N/A',
          populationWithoutAccessRural: 'N/A'
        });
      }
    } catch (error) {
      setCurrentData({
        countries: 0,
        gdp: '0',
        electricityAccess: '0',
        population: '0',
        perCapitaElectricity: 'N/A',
        cleanCooking: '0',
        electricityImport: 'N/A',
        electricityGeneration: 'N/A',
        electricityFossilFuels: 'N/A',
        renewableSources: 'N/A',
        solarWindTideWaveOtherSources: 'N/A',
        populationWithAccessUrban: 'N/A',
        populationWithAccessRural: 'N/A',
        populationWithoutAccessUrban: 'N/A',
        populationWithoutAccessRural: 'N/A'
      });
    } finally {
      setLoading(false);
    }
  }, [selectedYear, viewMode]);

  // Fetch data when year or view mode changes
  useEffect(() => {
    if (selectedYear) {
      if (viewMode === 'region') {
        // If we have a selected region, fetch data for that region with new year
        if (selectedRegion) {
          setHoveredArea(selectedRegion);
          fetchDataFromAPI(selectedRegion);
        } else {
          // Load North Africa data by default for region mode
          setHoveredArea('North Africa');
          setSelectedRegion('North Africa');
          fetchDataFromAPI('North Africa');
        }
      } else if (viewMode === 'country') {
        // If we have a selected country, fetch data for that country with new year
        if (selectedCountry) {
          setHoveredArea(selectedCountry);
          fetchDataFromAPI(null, selectedCountry);
        } else {
          // Load Algeria data by default for country mode
          setHoveredArea('Algeria');
          setSelectedCountry('Algeria');
          fetchDataFromAPI(null, 'Algeria');
        }
      }
    }
  }, [selectedYear, viewMode, fetchDataFromAPI, selectedRegion, selectedCountry]);

  // Track changes to trigger animation
  useEffect(() => {
    if (selectedYear !== null && previousYearRef.current !== null && previousYearRef.current !== selectedYear) {
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 2000);
    }
    if (selectedYear !== null) {
      previousYearRef.current = selectedYear;
    }
  }, [selectedYear]);

  useEffect(() => {
    if (previousRegionRef.current !== null && previousRegionRef.current !== selectedRegion) {
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 2000);
    }
    previousRegionRef.current = selectedRegion;
  }, [selectedRegion]);

  useEffect(() => {
    if (previousCountryRef.current !== null && previousCountryRef.current !== selectedCountry) {
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 2000);
    }
    previousCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  // Animation speed in milliseconds (lower = faster)
  const animationSpeed = 15;

  // Use animated numbers for all values
  const animatedElectricityGeneration = useAnimatedNumber(
    formatNumber(currentData.electricityGeneration), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedElectricityFossilFuels = useAnimatedNumber(
    formatNumber(currentData.electricityFossilFuels), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedRenewableSources = useAnimatedNumber(
    formatNumber(currentData.renewableSources), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedElectricityImport = useAnimatedNumber(
    formatNumber(currentData.electricityImport), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedGDP = useAnimatedNumber(
    formatGDP(currentData.gdp), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedCleanCooking = useAnimatedNumber(
    formatNumber(currentData.cleanCooking, true), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedPopulation = useAnimatedNumber(
    formatNumber(currentData.population), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedPerCapitaElectricity = useAnimatedNumber(
    formatNumber(currentData.perCapitaElectricity), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedPopulationWithAccessUrban = useAnimatedNumber(
    formatNumber(currentData.populationWithAccessUrban), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedPopulationWithAccessRural = useAnimatedNumber(
    formatNumber(currentData.populationWithAccessRural), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedPopulationWithoutAccessUrban = useAnimatedNumber(
    formatNumber(currentData.populationWithoutAccessUrban), 
    shouldAnimate && !loading, 
    animationSpeed
  );
  const animatedPopulationWithoutAccessRural = useAnimatedNumber(
    formatNumber(currentData.populationWithoutAccessRural), 
    shouldAnimate && !loading, 
    animationSpeed
  );

  const onEachRegion = (feature, layer) => {
    const subregion = feature.properties.subregion;
    
    // Map GeoJSON subregion names to API region names (same as backend)
    const subregionMapping = {
      'Middle Africa': 'Central Africa',
      'Eastern Africa': 'East Africa', 
      'Northern Africa': 'North Africa',
      'Southern Africa': 'Southern Africa',
      'Western Africa': 'West Africa'
    };
    
    const apiRegion = subregionMapping[subregion] || subregion;
    
    layer.on({
      click: (e) => {
        // Prevent region click from working in country mode
        if (viewMode === 'country') {
          return;
        }
        
        if (subregion) {
          setHoveredArea(apiRegion);
          setSelectedRegion(apiRegion);
          fetchDataFromAPI(apiRegion);
        }
      }
    });
  };

  const onEachCountry = (feature, layer) => {
    layer.on({
      click: (e) => {
        // Get country name from feature properties
        const countryName = feature.properties.NAME || 
                          feature.properties.name || 
                          feature.properties.admin || 
                          feature.properties.NAME_EN ||
                          feature.properties.ADMIN;
        
        // For country mode, use the country name directly for country-wise API
        if (countryName) {
          setHoveredArea(countryName); // Use country name as selected area
          setSelectedCountry(countryName); // Set selected country for highlighting
          fetchDataFromAPI(null, countryName); // Pass country name for country-wise API
        } else {
          setHoveredArea('Unknown Country');
        }
      }
    });
  };

  const MapContent = () => {
    const map = useMap();
    
    useEffect(() => {
      if (africaData) {
        // Set bounds to show Africa properly
        const bounds = [[-37, -20], [40, 55]];
        map.fitBounds(bounds, { padding: [10, 10] });
      }
    }, [map, africaData]);

    // Note: Zoom functionality removed as requested

    return null;
  };

  // Component to ensure selected region is brought to front and styled
  const SelectedRegionLayer = ({ geoData, selectedRegionName }) => {
    const map = useMap();
    
    useEffect(() => {
      if (geoData && selectedRegionName && viewMode === 'region') {
        // Find all layers and bring selected region to front
        map.eachLayer((layer) => {
          if (layer.feature) {
            const subregion = layer.feature.properties.subregion;
            const subregionMapping = {
              'Middle Africa': 'Central Africa',
              'Eastern Africa': 'East Africa',
              'Northern Africa': 'North Africa',
              'Southern Africa': 'Southern Africa',
              'Western Africa': 'West Africa'
            };
            const apiRegion = subregionMapping[subregion] || subregion;
            
            if (apiRegion === selectedRegionName) {
              // Bring to front for z-index
              layer.bringToFront();
              
              // Add class to path element for styling
              if (layer._path) {
                layer._path.classList.add('selected-region-border');
                // Ensure border is visible with orange color when region toggle is active
                layer._path.setAttribute('stroke-width', '1.2');
                layer._path.setAttribute('stroke', '#f98a00');
                layer._path.setAttribute('stroke-opacity', '1');
              }
            } else {
              // Remove class from unselected regions
              if (layer._path) {
                layer._path.classList.remove('selected-region-border');
              }
            }
          }
        });
      }
    }, [map, geoData, selectedRegionName, viewMode]);

    return null;
  };

  // Helper function to filter GeoJSON features by region
  const filterGeoJSONByRegion = (geoData, selectedRegionName) => {
    if (!geoData || !geoData.features) return { selected: null, unselected: geoData };
    
    const subregionMapping = {
      'Middle Africa': 'Central Africa',
      'Eastern Africa': 'East Africa',
      'Northern Africa': 'North Africa',
      'Southern Africa': 'Southern Africa',
      'Western Africa': 'West Africa'
    };
    
    const selectedFeatures = [];
    const unselectedFeatures = [];
    
    geoData.features.forEach(feature => {
      const subregion = feature.properties.subregion;
      const apiRegion = subregionMapping[subregion] || subregion;
      
      if (apiRegion === selectedRegionName) {
        selectedFeatures.push(feature);
      } else {
        unselectedFeatures.push(feature);
      }
    });
    
    const selectedGeoJSON = selectedFeatures.length > 0 ? {
      ...geoData,
      features: selectedFeatures
    } : null;
    
    const unselectedGeoJSON = {
      ...geoData,
      features: unselectedFeatures
    };
    
    return { selected: selectedGeoJSON, unselected: unselectedGeoJSON };
  };

  return (
    <section className="data-glance-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Data at a glance for <span style={{color:'#D48019'}}>Africa</span></h2>
            <p className="section-subtitle">Explore key economic and energy indicators by region and country.</p>
          </div>

        {/* Map Controls - Outside and Centered */}
             <div className="map-controls">
               <div className="map-toggle">
                  <button 
                    className={`toggle-btn ${viewMode === 'region' ? 'active' : ''}`}
                    onClick={() => {
                      setViewMode('region');
                      setSelectedCountry(null); // Clear country selection
                      // keep region if already selected
                    }}
                  >
                    Region
                  </button>
                  <button 
                    className={`toggle-btn ${viewMode === 'country' ? 'active' : ''}`}
                    onClick={() => {
                      setViewMode('country');
                      setSelectedRegion(null); // Clear region selection
                      // keep country if already selected
                    }}
                  >
                    Country
                  </button>
               </div>
               
               {/* Year Dropdown */}
               <div className="year-selector">
                 <label htmlFor="year-select">Year:</label>
                  <select 
                    id="year-select"
                    value={selectedYear || ''} 
                    onChange={(e) => {
                      setSelectedYear(e.target.value);
                    }}
                    className="year-dropdown"
                  >
                    <option value="">Select Year</option>
                   {[...new Set(availableYears.map(year => Number(year)))].sort((a, b) => b - a).map(year => (
                     <option key={year} value={year}>
                       {year}
                     </option>
                   ))}
                  </select>
                 <div style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                 </div>
               </div>
               
               {/* Region Dropdown (styled like year) */}
               {viewMode === 'region' && availableRegions.length > 0 && (
                 <div className="year-selector">
                   <label htmlFor="region-select">Region:</label>
                   <select
                     id="region-select"
                     value={selectedRegion || ''}
                     onChange={handleRegionChange}
                     className="region-dropdown"
                   >
                     {availableRegions.map(region => (
                       <option key={region} value={region}>{region}</option>
                     ))}
                   </select>
                 </div>
               )}

               {/* Country Dropdown (styled like year) */}
               {viewMode === 'country' && availableCountries.length > 0 && (
                 <div className="year-selector">
                   <label htmlFor="country-select">Country:</label>
                   <select
                     id="country-select"
                     value={selectedCountry || ''}
                     onChange={handleCountryChange}
                     className="country-dropdown"
                   >
                     {availableCountries.map(country => (
                       <option key={country} value={country}>{country}</option>
                     ))}
                   </select>
                 </div>
               )}
             </div>

        <div className="dashboard-content">
          {/* Left side - Map */}
           <div className="map-section">
             <div className="map-container">
               {africaData ? (
                 <MapContainer
                  // center={isMobile ? [5, 15] : [0, 22]}   // adjust center
                  // zoom={isMobile ? 2.5 : 3.6} 
                  //  minZoom={2}
                  //  maxZoom={6}
                  //  style={{ height: '380px', width: '100%' }}
                  zoom={isMobile ? 4.5 : 2.0} 
                  minZoom={isMobile ? 2.5 : 2.0}
                  maxZoom={isMobile ? 2.5 : 2.0}
                  style={{ height: '380px', width: '100%', minWidth: '100%', maxWidth: '100%' }}
                   zoomControl={false}
                   attributionControl={false}
                   scrollWheelZoom={false}
                   dragging={false}
                   bounds={[[-37, -20], [40, 55]]}
                   boundsOptions={{ padding: [10, 10] }}
                 >
                   {viewMode === 'region' ? (
                     // Render regions in two layers: unselected first, then selected on top
                     (() => {
                       const { selected: selectedRegionData, unselected: unselectedRegionData } = 
                         selectedRegion ? filterGeoJSONByRegion(africaData, selectedRegion) : 
                         { selected: null, unselected: africaData };
                       
                       return (
                         <>
                           {/* Render unselected regions first (lower z-index) */}
                           {unselectedRegionData && (
                             <GeoJSON
                               key="unselected-region-geojson"
                               data={unselectedRegionData}
                               style={getRegionStyle}
                               onEachFeature={onEachRegion}
                             />
                           )}
                           {/* Render selected region last (higher z-index) */}
                           {selectedRegionData && (
                             <GeoJSON
                               key="selected-region-geojson"
                               data={selectedRegionData}
                               style={getRegionStyle}
                               onEachFeature={onEachRegion}
                             />
                           )}
                           {/* Ensure selected region is brought to front */}
                           <SelectedRegionLayer geoData={africaData} selectedRegionName={selectedRegion} />
                         </>
                       );
                     })()
                   ) : (
                     <GeoJSON
                       key="country-geojson"
                       data={africaData}
                       style={getCountryStyle}
                       onEachFeature={onEachCountry}
                     />
                   )}
                   <MapContent />
                 </MapContainer>
               ) : null}
             </div>

            {/* {hoveredArea && (
              <div className="hover-info">
                <strong>{hoveredArea}</strong> - Hover to see data
              </div>
            )} */}
          </div>

          {/* Right side - Data Cards */}
          <div className="data-cards">
            <div className="selected-region-country">
              {hoveredArea ? (
                <>
                  <span className="selected-label">{viewMode === 'region' ? 'Region:' : 'Country:'}</span> <span className="selected-area">{hoveredArea}</span>
                </>
              ) : (
                <div style={{fontSize: '12px', color: '#666'}}>
                  {viewMode === 'region' ? '🌍 Click on a region to see data' : '🏳️ Click on a country to see data'}
                </div>
              )}
            </div>


            {/* 1. Electricity Generation */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/energy-access.png" alt="Energy access icon" width="60" height="60" />
                </div>
                <div className="card-content">
                <h3 className="card-title">Total Electricity Generation</h3>
                <div className="card-value">{animatedElectricityGeneration || '0'}<span className="card-unit">(GWh)</span></div>
                  <div className="card-description">
                  Total energy production
                </div>
              </div>
            </div>

            {/* 2. Electricity Fossil Fuels */}
              <div className="data-card">
                <div className="card-icon">
                <img src="/images/data-glance-icons/electricity-fossil-fuel.png" alt="Electricity fossil fuels icon" width="60" height="60" />
              </div>
              <div className="card-content">
                <h3 className="card-title">Electricity Fossil Fuels</h3>
                <div className="card-value">{animatedElectricityFossilFuels || '0'}<span className="card-unit">(GWh)</span></div>
                <div className="card-description">
                  Fossil fuel generation
                </div>
              </div>
            </div>

            {/* 3. Renewable Sources */}
            <div className="data-card">
              <div className="card-icon">
                <img src="/images/data-glance-icons/renewable-sources.png" alt="Renewable energy sources icon" width="60" height="60" />
                </div>
                <div className="card-content">
                <h3 className="card-title">Renewable Sources</h3>
                <div className="card-value">{animatedRenewableSources || '0'}<span className="card-unit">(GWh)</span></div>
                <div className="card-description solar-description">
                  {/* <p>Solar, wind, tide, wave and other sources:<span className="card-value-solar">{formatNumber(currentData.solarWindTideWaveOtherSources)} GWh</span></p> 
                */}
                <p>Solar, wind, tide, wave and other sources.</p>
                </div>
              </div>
            </div>

            {/* 4. Electricity Import */}
               <div className="data-card">
                 <div className="card-icon">
                <img src="/images/data-glance-icons/electricity-import.png" alt="Electricity import export icon" width="60" height="60" />
                 </div>
                 <div className="card-content">
                <h3 className="card-title">Electricity Import</h3>
                <div className="card-value">{animatedElectricityImport || 'N/A'}<span className="card-unit">(GWh)</span></div>
                <div className="card-description">
                  Energy import
                </div>
                 </div>
               </div>


            {/* 5. GDP */}
              <div className="data-card">
                <div className="card-icon">
                  <img src="/images/data-glance-icons/gdp.png" alt="GDP economic indicator icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">
                  {viewMode === 'region' ? 'GDP' : 'GDP'}
                  </h3>
                <div className="card-value">${animatedGDP || '0'}<span className="card-unit">(Million)</span></div>
                  <div className="card-description">
                  {viewMode === 'region' ? `Across ${currentData.countries} countries` : 'Economic indicator'}
                </div>
              </div>
            </div>


            {/* 6. Clean Cooking */}
            <div className="data-card">
              <div className="card-icon">
                <img src="/images/data-glance-icons/clean-cooking.png" alt="Clean cooking energy icon representing access to modern cooking solutions" width="60" height="60" />
              </div>
              <div className="card-content">
                <h3 className="card-title">Clean Cooking</h3>
                <div className="card-value">{animatedCleanCooking || '0'}<span className="card-unit">(%)</span></div>
                <div className="card-description">
                  Clean cooking access
                </div>
              </div>
            </div>

            {/* 7. Population */}
            <div className="data-card">
              <div className="card-icon">
                <img src="/images/data-glance-icons/population.png" alt="Population demographic icon" width="60" height="60" />
              </div>
              <div className="card-content">
                <div className="card-title-with-toggle">
                  <h3 className="card-title">Population</h3>
              </div>

                {/* Main Population Value */}
                <div className="card-value">
                  {animatedPopulation || '0'}
                  <span className="card-unit">(Million)</span>
            </div>
            
                {/* Urban and Rural Values */}
                <div className="population-toggle-section">
                {/* <p style={{fontSize: '9px', color: '#000'}}>Electricity</p> */}
                <div className="population-toggle-container">
                    <div className="population-toggle">
                      <span className={`toggle-label ${populationToggle === 'access' ? 'active' : ''}`}><span className='toggle-label' style={{color:'#000'}}>Electricity </span> Access</span>
                      <div 
                        className={`toggle-switch ${populationToggle === 'access' ? 'access' : 'without'}`}
                        onClick={() => setPopulationToggle(populationToggle === 'access' ? 'without' : 'access')}
                      >
                        <div className="toggle-thumb"></div>
                      </div>
                      <span className={`toggle-label ${populationToggle === 'without' ? 'active' : ''}`}>Without Access</span>
                    </div>
                  </div>
                <div className="population-values">
                   {/* Population Toggle */}
                  {populationToggle === 'access' ? (
                    <>
                      <div className="population-value">
                        <span className="population-label">Urban:</span>
                        <span className="population-number">{animatedPopulationWithAccessUrban || 'N/A'}<span className="card-unit pop-unit" style={{fontSize:'7px'}}>(M)</span></span>
                      </div>
                      <div className="population-value">
                        <span className="population-label">Rural:</span>
                        <span className="population-number">{animatedPopulationWithAccessRural || 'N/A'}<span className="card-unit pop-unit" style={{fontSize:'7px'}}>(M)</span></span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="population-value">
                        <span className="population-label">Urban:</span>
                        <span className="population-number">{animatedPopulationWithoutAccessUrban || 'N/A'}<span className="card-unit pop-unit" style={{fontSize:'7px'}}>(M)</span></span>
                      </div>
                      <div className="population-value">
                        <span className="population-label">Rural:</span>
                        <span className="population-number">{animatedPopulationWithoutAccessRural || 'N/A'}<span className="card-unit pop-unit" style={{fontSize:'7px'}}>(M)</span></span>
                      </div>
                    </>
                  )}
              </div>
                </div>
              </div>
            </div>

            {/* 8. Per Capita Electricity */}
            <div className="data-card">
              <div className="card-icon">
                <img src="/images/data-glance-icons/per-capita.png" alt="Per capita electricity consumption icon" width="60" height="60" />
              </div>
              <div className="card-content">
                <h3 className="card-title">Per Capita Electricity</h3>
                <div className="card-value">{animatedPerCapitaElectricity || 'N/A'}<span className="card-unit">(kWh)</span></div>
                <div className="card-description">
                  Total consumption
                </div>
              </div>
            </div>

            {/* 9. Read More Button - Only show in country mode */}
            {viewMode === 'country' && selectedCountry && (
              <div 
                className="data-card clickable-card"
                onClick={() => {
                  const urlCountry = countryToUrl(selectedCountry);
                  navigate(`/country-energy-profile/${urlCountry}`);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const urlCountry = countryToUrl(selectedCountry);
                    navigate(`/country-energy-profile/${urlCountry}`);
                  }
                }}
                aria-label={`View ${selectedCountry} energy profile`}
              >
                <div className="card-icon">
                  <img src="/images/data-glance-icons/view-more.png" alt="View more icon" width="60" height="60" />
                </div>
                <div className="card-content">
                  <h3 className="card-title">View Country Profile</h3>
                  <button
                    className="read-more-button"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent double navigation
                      const urlCountry = countryToUrl(selectedCountry);
                      navigate(`/country-energy-profile/${urlCountry}`);
                    }}
                  >
                    <span className="read-more-text">Click Here</span>
                    <span className="read-more-arrow-circle">
                      <svg width="8" height="8" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.5 3L7.5 6L4.5 9" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                  <div className="card-description">
                    Explore impact indicators 
                  </div>
                </div>
              </div>
            )}
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default DataGlance;
